import { BarChart3, LoaderCircle, Search, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";


const formatCurrency = (value) => {
  if (value == null) return "—";
  return `S$${Number(value).toLocaleString("en-SG", { maximumFractionDigits: 0 })}`;
};

const normalizeDistrict = (value) => {
  const cleaned = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!cleaned) return "";
  const numeric = cleaned.startsWith("D") ? cleaned.slice(1) : cleaned;
  const digits = numeric.replace(/\D/g, "");
  if (!digits) return cleaned;
  return `D${digits.padStart(2, "0")}`;
};

export const MarketIntelligenceWidget = ({ autoLoad = false, compact = false, defaultDistrictCode = "", subheading, testIdPrefix = "market-intelligence", title = "URA-backed market intelligence" }) => {
  const normalizedDefaultDistrict = useMemo(() => normalizeDistrict(defaultDistrictCode), [defaultDistrictCode]);
  const [districtCode, setDistrictCode] = useState(normalizedDefaultDistrict);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDistrictCode(normalizedDefaultDistrict);
  }, [normalizedDefaultDistrict]);

  const loadReport = useCallback(async (targetDistrict) => {
    const normalizedDistrict = normalizeDistrict(targetDistrict);
    if (!normalizedDistrict) {
      setError("Enter a valid district code such as D04 or D15.");
      setReport(null);
      setSearched(true);
      return;
    }

    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const response = await api.get("/market-intelligence/search", { params: { district: normalizedDistrict } });
      setReport(response.data);
    } catch {
      setReport(null);
      setError("Unable to load URA-backed market intelligence right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autoLoad || !normalizedDefaultDistrict) return;
    loadReport(normalizedDefaultDistrict);
  }, [autoLoad, loadReport, normalizedDefaultDistrict]);

  const panelClass = compact
    ? "rounded-[30px] border border-[#001935]/10 bg-white/88 p-6 shadow-[0_22px_70px_rgba(0,25,53,0.08)]"
    : "rounded-[34px] border border-[#001935]/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(249,245,236,0.96))] p-6 shadow-[0_24px_80px_rgba(0,25,53,0.08)] md:p-8";

  return (
    <section className={panelClass} data-testid={`${testIdPrefix}-widget`}>
      <div className={`grid gap-6 ${compact ? "xl:grid-cols-[0.9fr_1.1fr]" : "xl:grid-cols-[0.78fr_1.22fr]"}`}>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]" data-testid={`${testIdPrefix}-eyebrow`}>
            URA-backed research dashboard
          </p>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#001935] md:text-4xl" data-testid={`${testIdPrefix}-heading`}>
            {title}
          </h2>
          <p className="mt-4 text-sm leading-8 text-[#42566f]" data-testid={`${testIdPrefix}-subheading`}>
            {subheading || "Search by district code to see recent caveats, PSF signals, and a clean high-level transaction trend line alongside editorial commentary."}
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid={`${testIdPrefix}-search-shell`}>
            <label className="sr-only" htmlFor={`${testIdPrefix}-district-input`}>District code</label>
            <input
              className="h-12 w-full rounded-full border border-[#001935]/12 bg-white px-5 text-sm font-semibold tracking-[0.08em] text-[#001935] outline-none transition focus:border-[#D4AF37]"
              data-testid={`${testIdPrefix}-district-input`}
              id={`${testIdPrefix}-district-input`}
              onChange={(event) => setDistrictCode(event.target.value.toUpperCase())}
              placeholder="Enter district code e.g. D15"
              value={districtCode}
            />
            <button className="gold-button !min-w-[12rem] !justify-center" data-testid={`${testIdPrefix}-search-button`} onClick={() => loadReport(districtCode)} type="button">
              {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Search district
            </button>
          </div>

          {error ? (
            <p className="mt-4 rounded-[20px] border border-[#c85b4d]/18 bg-[#fff6f3] px-4 py-3 text-sm text-[#9f3f34]" data-testid={`${testIdPrefix}-error-message`}>
              {error}
            </p>
          ) : null}

          {!loading && searched && report && !report.report_available ? (
            <div className="mt-4 rounded-[24px] border border-[#D4AF37]/16 bg-[#fffaf1] px-4 py-4 text-sm leading-7 text-[#5f5a4e]" data-testid={`${testIdPrefix}-empty-state`}>
              {report.message}
            </div>
          ) : null}
        </div>

        <div>
          {loading ? (
            <div className="flex min-h-[18rem] items-center justify-center rounded-[28px] border border-[#001935]/10 bg-white/80" data-testid={`${testIdPrefix}-loading-state`}>
              <div className="flex items-center gap-3 text-sm font-semibold text-[#42566f]"><LoaderCircle className="h-5 w-5 animate-spin text-[#8b6d16]" /> Loading URA market intelligence...</div>
            </div>
          ) : report?.report_available ? (
            <div className="space-y-5" data-testid={`${testIdPrefix}-results-panel`}>
              <div className="rounded-[28px] border border-[#001935]/10 bg-[#001935] p-5 text-white" data-testid={`${testIdPrefix}-trend-card`}>
                <div className="flex items-center gap-2 text-[#F5D98C]"><TrendingUp className="h-4 w-4" /><p className="text-xs font-bold uppercase tracking-[0.18em]">Historical trend indicator</p></div>
                <p className="mt-4 text-lg font-semibold leading-8 text-white" data-testid={`${testIdPrefix}-trend-summary`}>
                  {report.trend_indicator?.summary_line}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-[24px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`${testIdPrefix}-stat-median-psf`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Median PSF</p>
                  <p className="mt-3 text-2xl font-semibold text-[#001935]">{report.median_psf ? formatCurrency(report.median_psf) : "—"}</p>
                </div>
                <div className="rounded-[24px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`${testIdPrefix}-stat-price-range`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Price Range</p>
                  <p className="mt-3 text-base font-semibold leading-7 text-[#001935]">{formatCurrency(report.price_range?.min)} — {formatCurrency(report.price_range?.max)}</p>
                </div>
                <div className="rounded-[24px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`${testIdPrefix}-stat-transaction-count`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Transaction Count</p>
                  <p className="mt-3 text-2xl font-semibold text-[#001935]">{report.transaction_count}</p>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[1.14fr_0.86fr]">
                <div className="rounded-[28px] border border-[#001935]/10 bg-white px-5 py-5" data-testid={`${testIdPrefix}-recent-transactions-card`}>
                  <div className="flex items-center gap-2 text-[#8b6d16]"><BarChart3 className="h-4 w-4" /><p className="text-xs font-bold uppercase tracking-[0.18em]">Recent transacted prices</p></div>
                  <div className="mt-4 space-y-3">
                    {report.recent_transactions?.slice(0, 5).map((transaction, index) => (
                      <div className="rounded-[22px] border border-[#001935]/8 bg-[#fffaf1] px-4 py-4" data-testid={`${testIdPrefix}-recent-transaction-${index + 1}`} key={transaction.transaction_id || `${transaction.project_name}-${index}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-[#001935]" data-testid={`${testIdPrefix}-recent-transaction-project-${index + 1}`}>{transaction.project_name || report.district_code}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#6b7a8c]" data-testid={`${testIdPrefix}-recent-transaction-meta-${index + 1}`}>
                              {transaction.contract_date ? new Date(transaction.contract_date).toLocaleDateString("en-SG") : "Recent caveat"} • {transaction.floor_range || "Floor range n/a"} • {transaction.unit_size_sqft ? `${Math.round(transaction.unit_size_sqft)} sqft` : "Size unavailable"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-[#001935]" data-testid={`${testIdPrefix}-recent-transaction-price-${index + 1}`}>{transaction.price_label || formatCurrency(transaction.price)}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#8b6d16]" data-testid={`${testIdPrefix}-recent-transaction-psf-${index + 1}`}>{transaction.psf ? `${formatCurrency(transaction.psf)} PSF` : "PSF unavailable"}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-[28px] border border-[#001935]/10 bg-white px-5 py-5" data-testid={`${testIdPrefix}-property-breakdown-card`}>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Property type breakdown</p>
                  <div className="mt-4 space-y-3">
                    {report.property_type_breakdown?.length ? report.property_type_breakdown.map((item, index) => (
                      <div className="flex items-center justify-between rounded-[20px] border border-[#001935]/8 bg-[#fffaf1] px-4 py-3" data-testid={`${testIdPrefix}-property-breakdown-item-${index + 1}`} key={`${item.property_type}-${index}`}>
                        <p className="text-sm font-semibold text-[#001935]">{item.property_type}</p>
                        <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#8b6d16]">{item.count}</p>
                      </div>
                    )) : <p className="text-sm leading-7 text-[#5f5a4e]" data-testid={`${testIdPrefix}-property-breakdown-empty`}>Property type splits will appear once matching transactions are loaded.</p>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[18rem] items-center justify-center rounded-[28px] border border-dashed border-[#001935]/12 bg-white/72 px-6 text-center text-sm leading-7 text-[#5f5a4e]" data-testid={`${testIdPrefix}-idle-state`}>
              Search a district to load recent caveats, median PSF, price range, and a quarter-on-quarter trend summary.
            </div>
          )}
        </div>
      </div>

      <p className="mt-5 text-xs leading-6 text-[#5f5a4e]" data-testid={`${testIdPrefix}-disclaimer`}>
        {report?.disclaimer || "Disclaimer: Market intelligence reports and transactional insights displayed on this platform are automatically generated utilizing historical data from caveats voluntarily lodged with the Singapore Land Authority (SLA) and published via the Urban Redevelopment Authority (URA) Data Service. While we strive to provide accurate and up-to-date data, these figures may not represent 100% of all market transactions and are intended strictly for educational and informational purposes. Users should independently verify all property transaction data before executing financial decisions."}
      </p>
    </section>
  );
};